import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { AppComponent } from '../app.component';
import { ApiService } from '../services/api.service';
import { HubService } from '../services/hub.service';
import { Hub } from '../types/hub';
import { HubIssue } from '../types/hubissue';
import { IssueActionType } from '../types/issue-action-type';
import { IssuePriority } from '../types/issue-priority';
import { IssueSource } from '../types/issue-source';
import { IssueStatus } from '../types/issue-status';
import { StandardResponse } from '../types/standard-response';
import { IssueTagAction } from '../types/issue-tag-action';
import { IssueTag } from '../types/issue-tag';

@Component({
  selector: 'hub-issues',
  templateUrl: './hubissues.component.html',
  styleUrls: ['./hubissues.component.css'],
  providers: [HubService],
})


export class HubIssuesComponent implements OnInit {
  @Input() hub: Hub;
  @ViewChild('issueSearchBox') searchBox: ElementRef;
  showNewIssuePanel = false;
  private allIssues = <HubIssue[]>[];
  hubIssues = <HubIssue[]>[];
  issue_statuses = <IssueStatus[]>[];
  issue_sources = <IssueSource[]>[];
  issue_priorities = <IssuePriority[]>[];
  issue_tag = <IssueTag[]>[];
  issue_action_type = <IssueTagAction[]>[];
  private action_types = <IssueActionType[]>[];
  private fromTimestamps: string[];
  private refreshFrom: string = null;
  showAllIssues = false;
  issueStatusFilter = 1;
  issuePriorityFilter = -1;
  errorMessage: string;
  private expanded: number;
  private issueSearchTerms = new Subject<string>();
  private issueSearch: Observable<HubIssue[]>;

  static handleRefreshError(err: any): void {
    if (err.status !== 401) {
      console.log('HubIssues Error: ' + err);
    }
  }

  constructor(
    private hubService: HubService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    if (!AppComponent.IsLoggedIn()) { return; }
    this.route.params.subscribe((params: Params) => {
      /*   if (params.status) {
           this.issueStatusFilter = +params.status;
         }*/
      if (params.priority) {
        this.issuePriorityFilter = +params.priority;
      }
    });
    try {
      this.hubService.getLookupTable('issue_source').subscribe(
        lookup_values => this.issue_sources = <IssueSource[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
      this.hubService.getLookupTable('issue_status').subscribe(
        lookup_values => this.issue_statuses = <IssueStatus[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
      this.hubService.getLookupTable('issue_action_type').subscribe(
        lookup_values => this.action_types = <IssueActionType[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
      this.hubService.getLookupTable('issue_priority').subscribe(
        lookup_values => this.issue_priorities = <IssuePriority[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
      this.hubService.getLookupTable('issue_tag').subscribe(
        lookup_values => this.issue_tag = <IssueTag[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
      this.hubService.getLookupTable('issue_action').subscribe(
        lookup_values => this.issue_action_type = <IssueTagAction[]>lookup_values,
        error => this.errorMessage = <any>error,
      );
    } catch (e) {
      console.log('Meh: ' + e);
    }
    this.later();
  }

  // @TODO: Stub implementation.  We don't know any actual statuses yet...
  isClosedStatus(stat: number): boolean {
    return stat === 3;
  }

  updateIssues(issues: HubIssue[]): void {
    for (let i = 0; i < issues.length; i++) {
      issues[i].hub_zone = issues[i].hub_postcode.toUpperCase().substr(0, 3);
    }
    this.allIssues = issues;
    this.filterIssues();
  }

  getTagType(tagID: number): string {
    for (let i = 0; i < this.issue_tag.length; i++)
      if (this.issue_tag[i].issue_tag_id == tagID)
        return this.issue_tag[i].issue_tag_name;
    return "-";
  }

  getActionType(actionID: number): string {
    for (let i = 0; i < this.issue_action_type.length; i++)
      if (this.issue_action_type[i].issue_action_id == actionID)
        return this.issue_action_type[i].issue_action_name;
    return "-";
  }

  filterIssues(): void {
    this.hubIssues = this.allIssues;
    this.priorityFilterIssues();
  }

  priorityFilterIssues(): void {
    this.issuePriorityFilter = +this.issuePriorityFilter;
    // console.log('Filtering issues for priority filter of ' + this.issuePriorityFilter);
    let issues = [];
    if (this.issuePriorityFilter > -1) {
      for (let i = 0; i < this.hubIssues.length; i++) {
        let issue = this.hubIssues[i];
        if (this.issuePriorityFilter !== issue.priority) {
          // console.log('Skipping issue with priority: ' + issue.priority);
          continue;
        }
        // console.log('Including issue with priority: ' + issue.priority);
        issues.push(issue);
      }
    } else {
      issues = this.hubIssues;
      // console.log('Including all issues.  There are ' + issues.length);
    }
    this.hubIssues = issues;
  }

  getColorFor(issue: HubIssue): string {
    let now = Date.now();
    let tomorrow = now + 60000000;
    try {
      if (this.expanded === issue.issue_id) { return '#afd4ce'; }
      if (this.isClosedStatus(issue.status)) { return '#84FFA8'; }
      if (now > Date.parse(issue.action_due)) { return '#ff7070'; }
      if (tomorrow > Date.parse(issue.action_due)) { return '#ffc080'; }
      if (this.errorMessage) { return '#ff8080'; } // So editor doesn't make me think this can be static
    } catch (e) { }
    return '#EEEEEE';
  }

  gotoHub(hubid: number): void {
    this.router.navigate(['/hub', hubid]);
  }

  refresh(status?: any): void {
    console.log("Refresh.......")
    console.log(this.issueStatusFilter)
    console.log(status)
    this.route.params.pipe(switchMap((params: Params) => this.fetchIssues(+params['hubid'], status)))
      .subscribe(
        (hubissues: HubIssue[]) => this.updateIssues(hubissues),
        (err: any) => HubIssuesComponent.handleRefreshError(err)
      );
  }

  fetchIssues(hubid: number, issue: number): Observable<HubIssue[]> {
    //this.issueStatusFilter = -1;
    this.issuePriorityFilter = -1;
    //console.log(this.refreshFrom);
    if (!isNaN(hubid) && issue == undefined) {
      issue = 5;
    } else if (issue == null) {
      issue = 1;
    }
    return this.hubService.getIssues(hubid, issue, null, this.refreshFrom);
  }

  followUp(val: boolean): string {
    if (val)
      return "Yes"
    else
      return "No"
  }

  later(): void {
    try {
      if (this.fromTimestamps === undefined) {
        this.fromTimestamps = [];
      }
      if (this.fromTimestamps.length > 0) {
        this.refreshFrom = this.fromTimestamps[this.fromTimestamps.length - 1];
        this.fromTimestamps = this.fromTimestamps.slice(0, -1);
        // console.log('Popped ' + this.refreshFrom);
      }
    } catch (err) {
      console.log(err);
    }
    this.refresh();
  }

  earlier(): void {
    try {
      if (!!this.allIssues && this.allIssues.length > 0) {
        if (!this.fromTimestamps) {
          this.fromTimestamps = [];
        }
        this.refreshFrom = this.allIssues[this.allIssues.length - 1].last_action;
        this.fromTimestamps.push(this.allIssues[0].last_action);
        // console.log('Pushed ' + this.allIssues[0].last_action);
      }
    } catch (err) {
      console.log(err);
    }
    this.refresh();
  }

  newIssue(): void {
    this.showNewIssuePanel = true;
  }

  newIssueVisible(): boolean {
    return this.showNewIssuePanel;
  }

  expandIssue(issue_id: number): void {
    if (this.expanded !== issue_id) {
      this.expanded = issue_id;
      // console.log('Expanding issue ' + this.expanded);
    }
  }

  onIssueSaved(issueChanged: boolean): void {
    // console.log("Issue was saved: "+issueChanged);
    if (issueChanged) {
      this.refresh();
    }
    this.showNewIssuePanel = false;
  }

  onIssueMinimised(issueChanged: boolean): void {
    // console.log('Minimising issue ' + this.expanded);
    this.expanded = 0;
    if (issueChanged) {
      this.refresh();
    }
  }

  newIssueClosed(issueChanged: boolean) {
    // console.log('Making new issue pane invisible');
    document.getElementById('new_issue').style.display = 'none';
    if (issueChanged) {
      this.refresh();
    }
  }

  // Push a search term into the observable stream.
  search(term: string): void {
    this.issueSearchTerms.next(term);
  }

  ngAfterViewInit(): void {
    this.resetSearch();
  }

  resetSearch(): void {
    if (this.searchBox && this.searchBox.nativeElement) {
      let searchInput = this.searchBox.nativeElement as HTMLInputElement;
      searchInput.value = '';
    }
    console.log(this.searchBox);
    this.issueSearch = this.issueSearchTerms.asObservable().pipe(
      debounceTime(300),        // wait 300ms after each keystroke before considering the term
      distinctUntilChanged(),   // ignore if next search term is same as previous
      switchMap((term: string) => term && term.length > 2   // switch to new observable each time the term changes
        // return the http search observable
        ? this.hubService.issueSearch(term)
        // or the observable of empty issues if there was no search term, or it was less than three characters
        : of(<HubIssue[]>([]))),
      catchError((error: { error: StandardResponse; status: number; }) => {
        // TODO: add real error handling
        if (error.error && error.status) {
          let response = error.error as StandardResponse;
          console.log('Fix error handling:' + JSON.stringify(response, null, 2));
          if (error.status === 401) {
            this.errorMessage = 'Unauthorized!';
            ApiService.AuthenticationExpired();
          } else {
            this.errorMessage = `The server returned a ${error.status} error code.`;
          }
        } else {
          this.errorMessage = 'An unexpected error occurred!';
          console.log('Fix error handling:' + JSON.stringify(error, null, 2));
        }
        return of(<HubIssue[]>([]));
      })
    );
  }

}

